import React from 'react';
import WarningDialogIcon from '@/assets/icons/WarningDialogIcon';
import WarningErrorIcon from '@/assets/icons/WarningErrorIcon';
import Iconify from '@/components/iconify/Iconify';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';

const baseDialogPropTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  action: PropTypes.node,
  content: PropTypes.node,
  onClose: PropTypes.func,
  cancelAction: PropTypes.node,
  icon: PropTypes.node,
};

ConfirmDialog.propTypes = {
  ...baseDialogPropTypes,
  variant: PropTypes.oneOf(['default', 'defaultDanger', 'products', 'productsDark']),
};

export default function ConfirmDialog({ title, content, action, open, onClose, variant, ...other }) {
  const renderDialog = () => {
    if (!variant) {
      return (
        <DialogDefault
          title={title}
          content={content}
          action={action}
          open={open}
          onClose={onClose}
          {...other}
        />
      );
    }

    switch (variant) {
      case 'defaultDanger':
        return (
          <DialogV2Danger
            title={title}
            content={content}
            action={action}
            open={open}
            onClose={onClose}
            {...other}
          />
        );
      case 'products':
        return (
          <DialogProducts
            title={title}
            content={content}
            action={action}
            open={open}
            onClose={onClose}
            {...other}
          />
        );
      case 'productsDark':
        return (
          <DialogProductsDark
            title={title}
            content={content}
            action={action}
            open={open}
            onClose={onClose}
            {...other}
          />
        );
      case 'default':
      default:
        return (
          <DialogV2
            title={title}
            content={content}
            action={action}
            open={open}
            onClose={onClose}
            {...other}
          />
        );
    }
  };

  return renderDialog();
}

function DialogDefault({ title, content, action, open, onClose, ...other }) {
  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose} {...other}>
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

      {content && <DialogContent sx={{ typography: 'body2' }}>{content}</DialogContent>}

      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          data-testid="confirm-dialog-close-button"
          onClick={onClose}
        >
          Cancelar
        </Button>
        {action}
      </DialogActions>
    </Dialog>
  );
}

DialogDefault.propTypes = {
  ...baseDialogPropTypes,
};

function DialogV2Base({ title, content, action, icon, open, onClose, titleSx, contentSx, paperSx, showCloseButton, cancelLabel = 'Cancelar', ...other }) {
  const actionNode = React.isValidElement(action)
    ? React.cloneElement(action, {
      variant: 'contained',
      sx: {
        minWidth: 120,
        height: 40,
        borderRadius: 1,
        fontSize: 16,
        lineHeight: '24px',
        backgroundColor: 'error.main',
        boxShadow: 'none',
        '&:hover': { backgroundColor: 'error.dark', boxShadow: 'none' },
      },
    })
    : action;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(6px)',
        },
        '& .MuiDialog-paper': {
          position: 'relative',
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          borderRadius: 2,
          px: 4,
          pt: 4,
          pb: 3.5,
          color: 'text.primary',
          ...paperSx,
        },
      }}
      {...other}
    >
      {showCloseButton && (
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Iconify icon="eva:close-fill" />
        </IconButton>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
        }}
      >
        {icon}

        <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, maxWidth: 360, ...titleSx }}>
          {title}
        </Typography>

        {content && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', maxWidth: 280, ...contentSx }}>
            {content}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center', width: '100%' }}>
          <Button
            variant="outlined"
            color="inherit"
            data-testid="confirm-dialog-close-button"
            onClick={onClose}
            sx={(theme) => ({
              minWidth: 120,
              height: 40,
              borderRadius: 1,
              fontSize: 16,
              lineHeight: '24px',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.32), backgroundColor: theme.palette.action.hover },
            })}
          >
            {cancelLabel}
          </Button>

          {actionNode}
        </Box>
      </Box>
    </Dialog>
  );
}

DialogV2Base.propTypes = {
  ...baseDialogPropTypes,
  icon: PropTypes.node,
  cancelLabel: PropTypes.node,
};

function DialogV2(props) {
  return <DialogV2Base icon={<WarningDialogIcon sx={{ width: 48, height: 48 }} />} {...props} />;
}

DialogV2.propTypes = {
  ...baseDialogPropTypes,
};

function DialogV2Danger(props) {
  return <DialogV2Base icon={<WarningErrorIcon sx={{ width: 48, height: 48 }} />} {...props} />;
}

DialogV2Danger.propTypes = {
  ...baseDialogPropTypes,
};

function DialogProducts({ title, content, cancelAction, icon, action, open, onClose, ...other }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '515px',
          maxWidth: 'none',
        },
      }}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
          width: '100%',
          // O título pode levar o nome do produto, de tamanho livre, então o miolo cresce com ele a
          // partir de 295px. Sem flexShrink, ele encolheria até o maxHeight do paper (coluna flex) e o
          // excesso vazaria para cima do centro, fora da rolagem; o py tira o título de baixo do X.
          minHeight: '295px',
          flexShrink: 0,
          py: 2,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
          }}
        >
          <Iconify icon="eva:close-fill" />
        </IconButton>

        {icon || <WarningErrorIcon />}

        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <DialogTitle sx={{ textAlign: 'center', fontSize: '24px', maxWidth: '320px', padding: 0 }}>
            {title}
          </DialogTitle>

          {content && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', maxWidth: '320px', textAlign: 'center' }}
            >
              {content}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
            paddingX: 4,
            maxWidth: '420px',
          }}
        >
          {!cancelAction && (
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              data-testid="confirm-dialog-close-button"
              onClick={onClose}
            >
              Voltar
            </Button>
          )}

          {cancelAction && (
            <Box sx={{ width: '100%' }} data-testid="confirm-dialog-close-button">
              {cancelAction}
            </Box>
          )}

          {action}
        </Box>
      </Box>
    </Dialog>
  );
}

DialogProducts.propTypes = {
  ...baseDialogPropTypes,
  name: PropTypes.string,
  cancelAction: PropTypes.node,
  icon: PropTypes.node,
};

function DialogProductsDark({ title, content, cancelAction, icon, action, open, onClose, hideCloseButton, ...other }) {
  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(6px)',
        },
        '& .MuiDialog-paper': {
          position: 'relative',
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          borderRadius: 2,
          px: 4,
          pt: 4,
          pb: 3.5,
          color: 'text.primary',
        },
      }}
      {...other}
    >
      {!hideCloseButton && (
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Iconify icon="eva:close-fill" />
        </IconButton>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2,
        }}
      >
        {icon}

        <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3, maxWidth: 360, mt: 1 }}>
          {title}
        </Typography>

        {content && (
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.7,
              color: 'text.secondary',
              maxWidth: { xs: 280, sm: 320 },
              whiteSpace: 'pre-line',
              mt: 0.75,
            }}
          >
            {content}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center', width: '100%' }}>
          {cancelAction && <Box sx={{ flex: 1 }}>{cancelAction}</Box>}
          {action && <Box sx={{ flex: 1 }}>{action}</Box>}
        </Box>
      </Box>
    </Dialog>
  );
}

DialogProductsDark.propTypes = {
  ...baseDialogPropTypes,
  cancelAction: PropTypes.node,
  icon: PropTypes.node,
  hideCloseButton: PropTypes.bool,
};
